import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  Row,
  Col,
  Descriptions,
  Typography,
  Table,
  Tag,
  Spin,
  Alert,
  Statistic,
  Divider,
  Progress,
  Space,
  Tabs,
  List,
  Badge,
  message,
} from "antd";
import {
  BankOutlined,
  UserOutlined,
  TeamOutlined,
  GlobalOutlined,
  DollarOutlined,
  RiseOutlined,
  FallOutlined,
  CheckCircleOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { useAuth0 } from "../../react-auth0-spa";
import useCompanyReportsService from "../../services/companyReportsService";
import IIC_CODES_IT from "../../config/iicCodesItalia";
import { generateStructuredPDF, generatePDFBlob } from "../../utils/reportPdfGenerator";
import moment from "moment";

const { Title, Text } = Typography;
const { TabPane } = Tabs;

const CompanyReport = ({ reportId, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const { getTokenSilently } = useAuth0();
  const companyReportsService = useCompanyReportsService();
  const reportContentRef = useRef(null);

  useEffect(() => {
    fetchReportDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportId]);

  useEffect(() => {
    // Listen for PDF generation event from parent
    const handleGeneratePDF = () => {
      generatePDF();
    };

    // Listen for PDF share event from parent
    const handleSharePDF = () => {
      sharePDF();
    };

    window.addEventListener('generatePDF', handleGeneratePDF);
    window.addEventListener('sharePDF', handleSharePDF);
    return () => {
      window.removeEventListener('generatePDF', handleGeneratePDF);
      window.removeEventListener('sharePDF', handleSharePDF);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [report]);

  const fetchReportDetails = async () => {
    try {
      setLoading(true);
      const token = await getTokenSilently();
      const response = await companyReportsService.getReportDetails(reportId, token, true);
      
      if (response.success && response.report) {
        setReport(response.report);
      }
    } catch (error) {
      console.error("Error fetching report details:", error);
      message.error("Errore nel caricamento del report");
    } finally {
      setLoading(false);
    }
  };

  const generatePDF = async () => {
    if (!report) {
      message.warning("Nessun report disponibile per la generazione PDF");
      return;
    }

    try {
      message.info("Generazione PDF in corso...");
      
      // Use structured PDF generation
      const fileName = generateStructuredPDF(report);
      
      message.success(`PDF scaricato: ${fileName}`);
    } catch (error) {
      console.error("Error generating PDF:", error);
      message.error("Errore nella generazione del PDF");
    }
  };

  const sharePDF = async () => {
    if (!report) {
      message.warning("Nessun report disponibile per la condivisione");
      return;
    }

    try {
      message.info("Preparazione del documento per la condivisione...");
      
      // Generate PDF as blob
      const { blob, fileName } = generatePDFBlob(report);
      const file = new File([blob], fileName, { type: 'application/pdf' });
      
      // Check if Web Share API is available
      if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({
            title: `Report ${report.fullData?.companyDetails?.companyName || ''}`,
            text: `Report aziendale di ${report.fullData?.companyDetails?.companyName || 'N/A'} - P.IVA: ${report.fullData?.companyDetails?.vatCode || 'N/A'}`,
            files: [file]
          });
          message.success("Report condiviso con successo");
        } catch (shareError) {
          // User cancelled the share or error occurred
          if (shareError.name !== 'AbortError') {
            console.error("Error sharing:", shareError);
            message.info("Condivisione annullata");
          }
        }
      } else {
        // Fallback: download the PDF if sharing is not available
        message.warning("La condivisione non è supportata su questo dispositivo. Il PDF verrà scaricato.");
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
        message.success(`PDF scaricato: ${fileName}`);
      }
    } catch (error) {
      console.error("Error sharing PDF:", error);
      message.error("Errore nella condivisione del PDF");
    }
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined || isNaN(value)) return "-";
    return new Intl.NumberFormat("it-IT", {
      style: "currency",
      currency: "EUR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const formatPercentage = (value) => {
    if (value === null || value === undefined) return "-";
    return `${value.toFixed(2)}%`;
  };

  const getColorByValue = (value, isPositiveBetter = true) => {
    if (value === null || value === undefined) return undefined;
    const isPositive = value > 0;
    if (isPositiveBetter) {
      return isPositive ? "#52c41a" : "#f5222d";
    } else {
      return isPositive ? "#f5222d" : "#52c41a";
    }
  };

  const calculateCompanyAge = () => {
    if (!report?.fullData?.companyDates) return null;
    
    const { registrationDate, incorporationDate, startDate } = report.fullData.companyDates;
    // Use the earliest date available
    const foundationDate = incorporationDate || registrationDate || startDate;
    
    if (!foundationDate) return null;
    
    const years = moment().diff(moment(foundationDate), 'years');
    const months = moment().diff(moment(foundationDate), 'months') % 12;
    
    return { years, months, foundationDate };
  };

  const renderCompanyBasicInfo = () => {
    if (!report?.fullData?.companyDetails) return null;
    
    const { companyDetails, address, contacts, webAndSocial, mail, companyDates } = report.fullData;
    
    return (
      <Card title={<><BankOutlined /> Informazioni Azienda</>} style={{ marginBottom: 24 }}>
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Ragione Sociale">
                <Text strong>{companyDetails.companyName}</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Partita IVA">
                {companyDetails.vatCode}
              </Descriptions.Item>
              <Descriptions.Item label="Codice Fiscale">
                {companyDetails.taxCode}
              </Descriptions.Item>
              <Descriptions.Item label="CCIAA">
                {companyDetails.cciaa}
              </Descriptions.Item>
              <Descriptions.Item label="REA">
                {companyDetails.reaCode}
              </Descriptions.Item>
              <Descriptions.Item label="PEC">
                {report.fullData.pec || "-"}
              </Descriptions.Item>
            </Descriptions>
          </Col>
          <Col span={12}>
            <Descriptions column={1} size="small">
              <Descriptions.Item label="Indirizzo">
                {address?.streetName}, {address?.zipCode} {address?.town} ({address?.province?.code})
              </Descriptions.Item>
              <Descriptions.Item label="Telefono">
                {contacts?.telephoneNumber || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Email">
                {mail?.email || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Sito Web">
                {webAndSocial?.website ? (
                  <a href={`https://${webAndSocial.website}`} target="_blank" rel="noopener noreferrer">
                    <GlobalOutlined /> {webAndSocial.website}
                  </a>
                ) : "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Forma Giuridica">
                {report.fullData.legalForm?.detailedLegalForm?.description || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Date Importanti">
                <Space direction="vertical" size="small">
                  {companyDates?.incorporationDate && (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Costituzione: {moment(companyDates.incorporationDate).format("DD/MM/YYYY")}
                    </Text>
                  )}
                  {companyDates?.startDate && (
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      Inizio Attività: {moment(companyDates.startDate).format("DD/MM/YYYY")}
                    </Text>
                  )}
                </Space>
              </Descriptions.Item>
            </Descriptions>
          </Col>
        </Row>
      </Card>
    );
  };

  const renderFinancialOverview = () => {
    if (!report?.fullData?.ecofin) return null;
    
    const { ecofin, operatingResults, annualResult } = report.fullData;
    const balanceSheetYear = ecofin.balanceSheetDate ? moment(ecofin.balanceSheetDate).year() : ecofin.turnoverYear;
    
    return (
      <Card 
        title={
          <Space>
            <DollarOutlined />
            <span>Panoramica Finanziaria</span>
            <Tag color="blue">Anno {balanceSheetYear}</Tag>
            {ecofin.balanceSheetDate && (
              <Text type="secondary" style={{ fontSize: 12 }}>
                (Bilancio al {moment(ecofin.balanceSheetDate).format("DD/MM/YYYY")})
              </Text>
            )}
          </Space>
        } 
        style={{ marginBottom: 24 }}
      >
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={6}>
            <Statistic
              title={`Fatturato ${balanceSheetYear}`}
              value={ecofin.turnover}
              formatter={(value) => formatCurrency(value)}
              prefix={ecofin.turnoverTrend > 0 ? <RiseOutlined /> : <FallOutlined />}
              valueStyle={{ color: getColorByValue(ecofin.turnoverTrend) }}
              suffix={<Text type="secondary" style={{ fontSize: 14 }}>({formatPercentage(ecofin.turnoverTrend)})</Text>}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Statistic
              title={`EBITDA ${balanceSheetYear}`}
              value={operatingResults?.ebitda}
              formatter={(value) => formatCurrency(value)}
              valueStyle={{ color: getColorByValue(operatingResults?.ebitda) }}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Statistic
              title="EBIT"
              value={operatingResults?.ebit}
              formatter={(value) => formatCurrency(value)}
              valueStyle={{ color: getColorByValue(operatingResults?.ebit) }}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Statistic
              title="Risultato d'Esercizio"
              value={annualResult?.find(item => item.code === "IIC179")?.value}
              formatter={(value) => formatCurrency(value)}
              valueStyle={{ color: getColorByValue(annualResult?.find(item => item.code === "IIC179")?.value) }}
            />
          </Col>
        </Row>
        <Divider />
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={6}>
            <Statistic
              title="Capitale Sociale"
              value={ecofin.shareCapital}
              formatter={(value) => formatCurrency(value)}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Statistic
              title="Patrimonio Netto"
              value={ecofin.netWorth}
              formatter={(value) => formatCurrency(value)}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Statistic
              title="Cash Flow"
              value={operatingResults?.cashFlow}
              formatter={(value) => formatCurrency(value)}
              valueStyle={{ color: getColorByValue(operatingResults?.cashFlow) }}
            />
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Statistic
              title="Dipendenti"
              value={report.fullData.employees?.employee || 0}
              suffix={<Text type="secondary">({report.fullData.employees?.employeeRange?.description})</Text>}
            />
          </Col>
        </Row>
      </Card>
    );
  };

  const renderKPIs = () => {
    if (!report?.fullData) return null;
    
    const kpiData = [
      {
        category: "Redditività",
        metrics: [
          { label: "ROE", value: report.fullData.profitability?.roe, format: "percentage", isGood: v => v > 10 },
          { label: "ROI", value: report.fullData.profitability?.roi, format: "percentage", isGood: v => v > 8 },
          { label: "ROS", value: report.fullData.profitability?.ros, format: "percentage", isGood: v => v > 5 },
          { label: "ROA Monetario", value: report.fullData.profitability?.roaMonetary, format: "percentage", isGood: v => v > 5 },
        ],
      },
      {
        category: "Liquidità",
        metrics: [
          { label: "Current Ratio", value: report.fullData.financialStability?.currentRatio, format: "number", isGood: v => v > 1.5 },
          { label: "Acid Test", value: report.fullData.financialStability?.acidTest, format: "number", isGood: v => v > 1 },
          { label: "Cash/Debiti a BT", value: report.fullData.liquidityRatios?.cashTotalShortTermDebt, format: "number", isGood: v => v > 0.2 },
        ],
      },
      {
        category: "Indebitamento",
        metrics: [
          { label: "Leverage", value: report.fullData.indebtedness?.leverage, format: "number", isGood: v => v < 2 },
          { label: "Debt Ratio", value: report.fullData.indebtedness?.debtRatio, format: "number", isGood: v => v < 0.6 },
          { label: "Bank Debt Ratio", value: report.fullData.indebtedness?.bankDebtRatio, format: "number", isGood: v => v < 0.3 },
        ],
      },
      {
        category: "Efficienza",
        metrics: [
          { label: "Turnover Index", value: report.fullData.efficiency?.turnoverIndex, format: "number", isGood: v => v > 1 },
          { label: "Rotazione Attivo Corrente", value: report.fullData.efficiency?.currentAssetsRotation, format: "number", isGood: v => v > 2 },
        ],
      },
    ];

    return (
      <Card title={<><RiseOutlined /> Indicatori Chiave (KPIs)</>} style={{ marginBottom: 24 }}>
        <Row gutter={[16, 16]}>
          {kpiData.map((category, idx) => (
            <Col key={idx} xs={24} md={12} lg={6}>
              <Card size="small" title={category.category} type="inner">
                {category.metrics.map((metric, midx) => {
                  const value = metric.value;
                  let displayValue = "-";
                  let status = "default";
                  
                  if (value !== null && value !== undefined) {
                    if (metric.format === "percentage") {
                      displayValue = formatPercentage(value);
                    } else if (metric.format === "currency") {
                      displayValue = formatCurrency(value);
                    } else {
                      displayValue = value.toFixed(2);
                    }
                    
                    if (metric.isGood && metric.isGood(value)) {
                      status = "success";
                    } else if (metric.isGood && !metric.isGood(value)) {
                      status = "error";
                    }
                  }
                  
                  return (
                    <div key={midx} style={{ marginBottom: 8 }}>
                      <Text type="secondary">{metric.label}:</Text>{" "}
                      <Badge status={status} text={<Text strong>{displayValue}</Text>} />
                    </div>
                  );
                })}
              </Card>
            </Col>
          ))}
        </Row>
      </Card>
    );
  };

  const renderManagementAndShareholders = () => {
    if (!report?.fullData) return null;
    
    const { managers = [], shareholders = [] } = report.fullData;
    
    return (
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} lg={12}>
          <Card title={<><UserOutlined /> Management</>} style={{ marginBottom: 24 }}>
            <List
              dataSource={managers}
              renderItem={(manager) => {
                const managerName = manager.companyName || 
                  `${manager.name || ''} ${manager.surname || ''}`.trim() || 
                  "N/A";
                const isCompany = manager.companyName ? true : false;
                
                return (
                  <List.Item>
                    <List.Item.Meta
                      title={managerName}
                      description={
                        <Space direction="vertical" size="small">
                          <Text type="secondary">
                            {isCompany ? "P.IVA" : "CF"}: {manager.taxCode}
                          </Text>
                          {!isCompany && manager.birthDate && (
                            <Text type="secondary">
                              Nato il {moment(manager.birthDate).format("DD/MM/YYYY")} a {manager.birthTown}
                            </Text>
                          )}
                          {manager.roles && manager.roles.map((role, idx) => (
                            <Tag key={idx} color="blue">
                              {role.role.description}
                            </Tag>
                          ))}
                          {manager.isLegalRepresentative && (
                            <Tag color="green" icon={<CheckCircleOutlined />}>
                              Rappresentante Legale
                            </Tag>
                          )}
                        </Space>
                      }
                    />
                  </List.Item>
                );
              }}
            />
          </Card>
        </Col>
        
        <Col xs={24} lg={12}>
          <Card title={<><TeamOutlined /> Soci</>} style={{ marginBottom: 24 }}>
            <List
              dataSource={shareholders}
              renderItem={(shareholder) => {
                const info = shareholder.shareholdersInformation?.[0];
                const shareholderName = info ? 
                  (info.companyName || `${info.name || ''} ${info.surname || ''}`.trim() || "N/A") : 
                  "N/A";
                const isCompany = info?.companyName ? true : false;
                
                return (
                  <List.Item>
                    <List.Item.Meta
                      title={shareholderName}
                      description={
                        <Space direction="vertical" size="small">
                          {info && (
                            <Text type="secondary">
                              {isCompany ? "P.IVA" : "CF"}: {info.taxCode}
                            </Text>
                          )}
                          {info && info.streetName && (
                            <Text type="secondary">
                              {info.streetName}, {info.zipCode} {info.town}
                            </Text>
                          )}
                          <Progress
                            percent={shareholder.percentShare}
                            size="small"
                            format={(percent) => `${percent}%`}
                          />
                        </Space>
                      }
                    />
                  </List.Item>
                );
              }}
            />
          </Card>
        </Col>
      </Row>
    );
  };

  const renderFinancialStatements = () => {
    if (!report?.fullData) return null;
    
    const prepareTableData = (items) => {
      if (!items) return [];
      if (Array.isArray(items)) {
        return items.map((item, index) => {
          const code = item.code || index;
          return {
            key: code,
            code: code,
            label: IIC_CODES_IT[code] || code,
            value: item.value,
          };
        });
      }
      return Object.entries(items).map(([key, value]) => {
        const code = Array.isArray(value) ? value[0]?.code : key;
        return {
          key,
          code: code,
          label: IIC_CODES_IT[code] || code,
          value: Array.isArray(value) ? value[0]?.value : value,
        };
      });
    };

    const incomeStatementData = [
      { section: "Valore della Produzione", data: report.fullData.productionValue },
      { section: "Costi della Produzione", data: report.fullData.productionCosts },
      { section: "Proventi e Oneri Finanziari", data: report.fullData.revenuesFinancialCharges },
      { section: "Risultato d'Esercizio", data: report.fullData.annualResult },
    ];

    const balanceSheetData = [
      { section: "Immobilizzazioni Immateriali", data: report.fullData.intangibleFixedAssets },
      { section: "Immobilizzazioni Materiali", data: report.fullData.tangibleFixedAssets },
      { section: "Crediti", data: report.fullData.credits },
      { section: "Disponibilità Liquide", data: report.fullData.cashEquivalents },
      { section: "Patrimonio Netto", data: report.fullData.netWorth },
      { section: "Debiti", data: report.fullData.debts },
    ];

    const columns = [
      {
        title: "Voce",
        dataIndex: "label",
        key: "label",
        ellipsis: true,
      },
      {
        title: "Valore",
        dataIndex: "value",
        key: "value",
        align: "right",
        width: 150,
        render: (value) => formatCurrency(value),
      },
    ];

    const { ecofin } = report.fullData;
    const balanceSheetYear = ecofin?.balanceSheetDate ? moment(ecofin.balanceSheetDate).year() : ecofin?.turnoverYear;

    return (
      <Card 
        title={
          <Space>
            <BankOutlined />
            <span>Bilancio</span>
            <Tag color="green">Anno {balanceSheetYear || "N/A"}</Tag>
          </Space>
        } 
        style={{ marginBottom: 24 }}
      >
        <Tabs defaultActiveKey="1">
          <TabPane tab="Conto Economico" key="1">
            {incomeStatementData.map((section, idx) => (
              <div key={idx} style={{ marginBottom: 24 }}>
                <Title level={5}>{section.section}</Title>
                <Table
                  dataSource={prepareTableData(section.data)}
                  columns={columns}
                  pagination={false}
                  size="small"
                />
              </div>
            ))}
          </TabPane>
          <TabPane tab="Stato Patrimoniale" key="2">
            {balanceSheetData.map((section, idx) => (
              <div key={idx} style={{ marginBottom: 24 }}>
                <Title level={5}>{section.section}</Title>
                <Table
                  dataSource={prepareTableData(section.data)}
                  columns={columns}
                  pagination={false}
                  size="small"
                />
              </div>
            ))}
          </TabPane>
        </Tabs>
      </Card>
    );
  };

  const renderAdditionalInfo = () => {
    if (!report?.fullData) return null;
    
    const { atecoClassification, internationalClassification, ecofin, companyDates } = report.fullData;
    const companyAge = calculateCompanyAge();
    
    return (
      <Card title={<><InfoCircleOutlined /> Informazioni Aggiuntive</>} style={{ marginBottom: 24 }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} md={12}>
            <Descriptions column={1} size="small" bordered>
              <Descriptions.Item label="Codice ATECO">
                {atecoClassification?.ateco?.code} - {atecoClassification?.ateco?.description}
              </Descriptions.Item>
              <Descriptions.Item label="Codice NACE">
                {internationalClassification?.nace?.code} - {internationalClassification?.nace?.description}
              </Descriptions.Item>
              <Descriptions.Item label="RAE">
                {report.fullData.rae?.code} - {report.fullData.rae?.description}
              </Descriptions.Item>
              <Descriptions.Item label="SAE">
                {report.fullData.sae?.code} - {report.fullData.sae?.description}
              </Descriptions.Item>
            </Descriptions>
          </Col>
          <Col xs={24} md={12}>
            <Descriptions column={1} size="small" bordered>
              <Descriptions.Item label="Data Costituzione">
                {companyDates?.incorporationDate ? moment(companyDates.incorporationDate).format("DD/MM/YYYY") : "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Data Inizio Attività">
                {companyDates?.startDate ? (
                  <div>
                    <div>{moment(companyDates.startDate).format("DD/MM/YYYY")}</div>
                    {companyAge && (
                      <Text strong>
                        {companyAge.years} anni {companyAge.months > 0 && `e ${companyAge.months} mesi`}
                      </Text>
                    )}
                  </div>
                ) : "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Dimensione Impresa">
                {ecofin?.enterpriseSize?.description}
              </Descriptions.Item>
              <Descriptions.Item label="Range Fatturato">
                {ecofin?.turnoverRange?.description}
              </Descriptions.Item>
            </Descriptions>
          </Col>
        </Row>
      </Card>
    );
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: "50px" }}>
        <Spin size="large" tip="Caricamento report in corso..." />
      </div>
    );
  }

  if (!report || !report.fullData) {
    return (
      <Alert
        message="Report non disponibile"
        description="Il report richiesto non contiene dati completi."
        type="warning"
        showIcon
      />
    );
  }

  return (
    <div style={{ padding: "24px", position: "relative" }} ref={reportContentRef}>
      <Alert
        message={`Report generato il ${moment(report.completedAt).format("DD/MM/YYYY HH:mm")}`}
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
      />

      {renderCompanyBasicInfo()}
      {renderFinancialOverview()}
      {renderKPIs()}
      {renderManagementAndShareholders()}
      {renderFinancialStatements()}
      {renderAdditionalInfo()}
    </div>
  );
};

export default CompanyReport;